/* eslint-disable no-param-reassign */
import { WritableDraft } from 'immer';
import {
  DatasourceUpgradeDiff,
  DatasourceUpgradeStatus,
  DatasourceUpgradeStringFieldDiff,
} from '../../datasourceUpgrade.types';
import { getUpgradeFieldStatus } from '../../datasourceUpgradeHelper';
import { Datasource, Datasource_Preset } from '@/types/api';

export function setFormatText(state: DatasourceUpgradeDiff) {
  const { status, upgradePresetValue: upgradePresetValue } =
    state.spec.autoloader.format;
  if (status === 'unchanged') {
    return;
  }
  state.spec.autoloader.format.title = 'Autoloader format';
  state.spec.autoloader.format.description = `Autoloader format changed to ${upgradePresetValue}`;
}

export function setSchemaFileText(state: DatasourceUpgradeDiff) {
  const {
    status,
    upgradePresetValue: upgradePresetValue,
    currentPresetValue: currentPresetValue,
  } = state.spec.autoloader.schemaFile;
  if (status === 'unchanged') {
    return;
  }
  state.spec.autoloader.schemaFile.title = 'Schema file';
  if (currentPresetValue && upgradePresetValue) {
    state.spec.autoloader.schemaFile.description = 'Schema file changed';
    return;
  }

  if (upgradePresetValue) {
    if (state.spec.autoloader.cloudFiles.schemaHintsFile.currentPresetValue) {
      state.spec.autoloader.schemaFile.description =
        'Schema hints file changed to schema file';
      return;
    }

    if (state.spec.autoloader.cloudFiles.schemaHints.currentPresetValue) {
      state.spec.autoloader.schemaFile.description =
        'Schema hints changed to schema file';
      return;
    }

    state.spec.autoloader.schemaFile.description = 'Schema file added';
  }
}

export function setSchemaHintsFileText(state: DatasourceUpgradeDiff) {
  const {
    status,
    upgradePresetValue: upgradePresetValue,
    currentPresetValue: currentPresetValue,
  } = state.spec.autoloader.cloudFiles.schemaHintsFile;
  if (status === 'unchanged') {
    return;
  }

  state.spec.autoloader.cloudFiles.schemaHintsFile.title = 'Schema hints file';
  if (currentPresetValue && upgradePresetValue) {
    state.spec.autoloader.cloudFiles.schemaHintsFile.description =
      'Schema hints file changed';
    return;
  }

  if (upgradePresetValue) {
    if (state.spec.autoloader.schemaFile.currentPresetValue) {
      state.spec.autoloader.cloudFiles.schemaHintsFile.description =
        'Schema file changed to schema hints file';
      return;
    }

    if (state.spec.autoloader.cloudFiles.schemaHints.currentPresetValue) {
      state.spec.autoloader.cloudFiles.schemaHintsFile.description =
        'Schema hints changed to schema hints file';
      return;
    }

    state.spec.autoloader.cloudFiles.schemaHintsFile.description =
      'Schema hints file added';
  }
}

export function setSchemaHintsText(state: DatasourceUpgradeDiff) {
  const {
    status,
    upgradePresetValue: upgradePresetValue,
    currentPresetValue: currentPresetValue,
  } = state.spec.autoloader.cloudFiles.schemaHints;
  if (status === 'unchanged') {
    return;
  }
  state.spec.autoloader.cloudFiles.schemaHints.title = 'Schema hints';
  if (currentPresetValue && upgradePresetValue) {
    state.spec.autoloader.cloudFiles.schemaHints.description =
      'Schema hints changed';
    return;
  }

  if (upgradePresetValue) {
    if (state.spec.autoloader.cloudFiles.schemaHintsFile.currentPresetValue) {
      state.spec.autoloader.cloudFiles.schemaHints.description =
        'Schema hints file changed to schema hints';
      return;
    }

    if (state.spec.autoloader.schemaFile.currentPresetValue) {
      state.spec.autoloader.cloudFiles.schemaHints.description =
        'Schema file changed to schema hints';
      return;
    }

    state.spec.autoloader.cloudFiles.schemaHints.description =
      'Schema hints added';
  }
}

export function setAutoloaderText(state: DatasourceUpgradeDiff) {
  setFormatText(state);
  setSchemaFileText(state);
  setSchemaHintsFileText(state);
  setSchemaHintsText(state);
}

export const setFormatChanges = (
  datasource: WritableDraft<Datasource>,
  value: string,
) => {
  datasource.spec.autoloader = {
    ...datasource.spec.autoloader,
    location: datasource.spec.autoloader?.location || '',
    format: value,
  };
};

export const setSchemaHintsChanges = (
  datasource: WritableDraft<Datasource>,
  value: string | undefined,
) => {
  datasource.spec.autoloader = {
    ...datasource.spec.autoloader,
    location: datasource.spec.autoloader?.location || '',
    cloudFiles: {
      schemaHints: value,
    },
  };
};

export const setSchemaHintsFileChanges = (
  datasource: WritableDraft<Datasource>,
  value: string | undefined,
) => {
  datasource.spec.autoloader = {
    ...datasource.spec.autoloader,
    location: datasource.spec.autoloader?.location || '',
    cloudFiles: {
      schemaHintsFile: value,
    },
  };
};

export const setSchemaFileChanges = (
  datasource: WritableDraft<Datasource>,
  value: string | undefined,
) => {
  datasource.spec.autoloader = {
    ...datasource.spec.autoloader,
    location: datasource.spec.autoloader?.location || '',
    schema: value,
  };
};

export const getAutoloaderStatus = (
  datasource: Datasource,
  currentPresetValue: Datasource_Preset,
  upgradePresetValue: Datasource_Preset,
) => {
  const autoloaderStatus: DatasourceUpgradeStatus['spec']['autoloader'] = {
    format: getUpgradeFieldStatus({
      currentPresetValue: currentPresetValue?.autoloader?.format,
      upgradePresetValue: upgradePresetValue?.autoloader?.format,
      currentValue: datasource.spec.autoloader?.format,
    }),
    schemaFile: getUpgradeFieldStatus({
      currentPresetValue: currentPresetValue?.autoloader?.schemaFile,
      upgradePresetValue: upgradePresetValue?.autoloader?.schemaFile,
      currentValue: datasource.spec.autoloader?.schemaFile,
    }),
    cloudFiles: {
      schemaHintsFile: getUpgradeFieldStatus({
        currentPresetValue:
          currentPresetValue?.autoloader?.cloudFiles?.schemaHintsFile,
        upgradePresetValue:
          upgradePresetValue?.autoloader?.cloudFiles?.schemaHintsFile,
        currentValue: datasource.spec.autoloader?.cloudFiles?.schemaHintsFile,
      }),
      schemaHints: getUpgradeFieldStatus({
        currentPresetValue:
          currentPresetValue?.autoloader?.cloudFiles?.schemaHints,
        upgradePresetValue:
          upgradePresetValue?.autoloader?.cloudFiles?.schemaHints,
        currentValue: datasource.spec.autoloader?.cloudFiles?.schemaHints,
      }),
    },
  };

  return autoloaderStatus;
};

export const getAutoloaderDiff = (
  currentDatasource: Datasource,
  upgradeDatasource: Datasource,
  currentPresetValue: Datasource_Preset,
  upgradePresetValue: Datasource_Preset,
  status: DatasourceUpgradeStatus,
) => {
  const autoloaderDiff: DatasourceUpgradeDiff['spec']['autoloader'] = {
    format: {
      currentPresetValue: currentPresetValue?.autoloader?.format || '',
      upgradePresetValue: upgradePresetValue?.autoloader?.format || '',
      currentValue: currentDatasource.spec.autoloader?.format || '',
      upgradeValue: upgradeDatasource.spec.autoloader?.format || '',
      status: status.spec.autoloader.format,
      updateStatus: ({ draft, status: newStatus }) =>
        (draft.spec.autoloader.format = newStatus),
      type: 'string',
      setChanges: (params: {
        diff: DatasourceUpgradeStringFieldDiff;
        datasource: WritableDraft<Datasource>;
        value: string | undefined;
      }) => setFormatChanges(params.datasource, params.value || ''),
      inputType: 'fixed',
    },
    schemaFile: {
      currentPresetValue:
        currentPresetValue?.autoloader?.schemaFile || undefined,
      upgradePresetValue:
        upgradePresetValue?.autoloader?.schemaFile || undefined,
      currentValue: currentDatasource.spec.autoloader?.schemaFile || undefined,
      upgradeValue: upgradeDatasource.spec.autoloader?.schemaFile || undefined,
      status: status.spec.autoloader.schemaFile,
      title: 'Schema file',
      description: 'Schema file changed',
      inputType: 'textbox',
      updateStatus: ({ draft, status: newStatus }) =>
        (draft.spec.autoloader.schemaFile = newStatus),
      type: 'string',
      setChanges: (params: {
        diff: DatasourceUpgradeStringFieldDiff;
        datasource: WritableDraft<Datasource>;
        value: string | undefined;
      }) => setSchemaFileChanges(params.datasource, params.value),
    },
    cloudFiles: {
      schemaHintsFile: {
        currentPresetValue:
          currentPresetValue?.autoloader?.cloudFiles?.schemaHintsFile ||
          undefined,
        upgradePresetValue:
          upgradePresetValue?.autoloader?.cloudFiles?.schemaHintsFile ||
          undefined,
        currentValue:
          currentDatasource.spec.autoloader?.cloudFiles?.schemaHintsFile ||
          undefined,
        upgradeValue:
          upgradeDatasource.spec.autoloader?.cloudFiles?.schemaHintsFile ||
          undefined,
        status: status.spec.autoloader.cloudFiles.schemaHintsFile,
        updateStatus: ({ draft, status: newStatus }) =>
          (draft.spec.autoloader.cloudFiles.schemaHintsFile = newStatus),
        type: 'string',
        inputType: 'textbox',
        setChanges: (params: {
          diff: DatasourceUpgradeStringFieldDiff;
          datasource: WritableDraft<Datasource>;
          value: string | undefined;
        }) => setSchemaHintsFileChanges(params.datasource, params.value),
      },
      schemaHints: {
        currentPresetValue:
          currentPresetValue?.autoloader?.cloudFiles?.schemaHints || undefined,
        upgradePresetValue:
          upgradePresetValue?.autoloader?.cloudFiles?.schemaHints || undefined,
        currentValue:
          currentDatasource.spec.autoloader?.cloudFiles?.schemaHints ||
          undefined,
        upgradeValue:
          upgradeDatasource.spec.autoloader?.cloudFiles?.schemaHints ||
          undefined,
        status: status.spec.autoloader.cloudFiles.schemaHints,
        updateStatus: ({ draft, status: newStatus }) =>
          (draft.spec.autoloader.cloudFiles.schemaHints = newStatus),
        type: 'string',
        setChanges: (params: {
          diff: DatasourceUpgradeStringFieldDiff;
          datasource: WritableDraft<Datasource>;
          value: string | undefined;
        }) => setSchemaHintsChanges(params.datasource, params.value),
        inputType: 'textarea',
      },
    },
  };

  return autoloaderDiff;
};
